import { lazy, Suspense } from 'react';
import RouteSkeleton from './components/RouteSkeleton';

function loadPage(loader) {
  const Page = lazy(loader);
  return function LoadingPage(props) {
    return <Suspense fallback={<RouteSkeleton />}><Page {...props} /></Suspense>;
  };
}

import { PresentationProvider } from './presentation/PresentationProvider';
const AdminDirectory = loadPage(() => import('./pages/admin/AdminDirectory'));
const AdminUserDetail = loadPage(() => import('./pages/admin/AdminUserDetail'));
const AdminPartners = loadPage(() => import('./pages/admin/AdminPartners'));
const AdminDashboard = loadPage(() => import('./pages/admin/AdminDashboard'));
const PublicProfile = loadPage(() => import('./pages/PublicProfile'));
import ErrorPage from './components/ErrorPage';
import DialogProvider from './components/DialogProvider';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
// Layouts
import PublicLayout from './components/PublicLayout';
import Layout from './components/Layout';
import SystemAdminLayout from './components/SystemAdminLayout';
import PartnerLayout from './components/PartnerLayout';
import ProtectedRoute from './components/ProtectedRoute';
import ScrollToTop from './components/ScrollToTop';

// Public & Auth Pages
const Landing = loadPage(() => import('./pages/Landing'));
const Partners = loadPage(() => import('./pages/Partners'));
const Register = loadPage(() => import('./pages/auth/Register'));
const Login = loadPage(() => import('./pages/auth/Login'));
const ForgotPassword = loadPage(() => import('./pages/auth/ForgotPassword'));
const ResetPassword = loadPage(() => import('./pages/auth/ResetPassword'));
const ConfirmEmail = loadPage(() => import('./pages/auth/ConfirmEmail'));
const GameMechanics = loadPage(() => import('./pages/GameMechanics'));
const ClassesAndRanks = loadPage(() => import('./pages/ClassesAndRanks'));
const RanksGuide = loadPage(() => import('./pages/RanksGuide'));
const FAQ = loadPage(() => import('./pages/FAQ'));
const CategoriesGuide = loadPage(() => import('./pages/CategoriesGuide'));
const GoodDeedExamples = loadPage(() => import('./pages/GoodDeedExamples'));

// Adventurer App Pages (Logged In)
const Dashboard = loadPage(() => import('./pages/Dashboard'));
const CategoryDetail = loadPage(() => import('./pages/CategoryDetail'));
const LevelStatistics = loadPage(() => import('./pages/LevelStatistics'));
const HeroicActionSlots = loadPage(() => import('./pages/HeroicActionSlots'));
const Talents = loadPage(() => import('./pages/Talents'));
const HeroicActions = loadPage(() => import('./pages/HeroicActions'));
const QuickHeroicAction = loadPage(() => import('./pages/QuickHeroicAction'));
const EventsPage = loadPage(() => import('./components/SystemEvents'));
const Tavern = loadPage(() => import('./pages/Tavern'));
const SystemNewsDetail = loadPage(() => import('./pages/SystemNewsDetail'));
const Guilds = loadPage(() => import('./pages/Guilds'));
const CreateGuild = loadPage(() => import('./pages/CreateGuild'));
const GuildDetail = loadPage(() => import('./pages/GuildDetail'));
const GroupActionGuide = loadPage(() => import('./pages/GroupActionGuide'));
const NewGuildHeroicAction = loadPage(() => import('./pages/NewGuildHeroicAction'));
const PartnerDetail = loadPage(() => import('./pages/PartnerDetail'));
const AdventurerRedemptions = loadPage(() => import('./pages/AdventurerRedemptions'));
const WorldMap = loadPage(() => import('./pages/Map'));
const Profile = loadPage(() => import('./pages/Profile'));
const Notifications = loadPage(() => import('./pages/Notifications'));
const Legal = loadPage(() => import('./pages/Legal'));
const Chats = loadPage(() => import('./pages/Chats'));

// Role Portal Pages
const AdminKarma = loadPage(() => import('./pages/admin/AdminKarma'));
const AdminTribunalModerators = loadPage(() => import('./pages/admin/AdminTribunalModerators'));
const AdminSettings = loadPage(() => import('./pages/admin/AdminSettings'));
const AdminEvents = loadPage(() => import('./pages/admin/AdminEvents'));
const AdminHeroicActions = loadPage(() => import('./pages/admin/AdminHeroicActions'));
const AdminAppeals = loadPage(() => import('./pages/admin/AdminAppeals'));
const AdminXpAudits = loadPage(() => import('./pages/admin/AdminXpAudits'));
const PartnerPOS = loadPage(() => import('./pages/PartnerPOS'));
const PartnerOffers = loadPage(() => import('./pages/partner/PartnerOffers'));
const PartnerOfferEditor = loadPage(() => import('./pages/partner/PartnerOfferEditor'));
const AdminClassCategoryManager = loadPage(() => import('./pages/admin/AdminClassCategoryManager'));
const PartnerProfile = loadPage(() => import('./pages/partner/PartnerProfile'));
const PartnerSettings = loadPage(() => import('./pages/partner/PartnerSettings'));
const PartnerRedemptions = loadPage(() => import('./pages/partner/PartnerRedemptions'));
import { useCurrentUser } from './hooks/useCurrentUser';

import CookieBanner from './components/CookieBanner';

function HomeRoute() {
  const { isLoggedIn } = useCurrentUser();
  return isLoggedIn ? <Navigate to="/dashboard" replace /> : <Landing />;
}

function App() {
  return (
    <Router>
      <PresentationProvider>
      <ScrollToTop />
      <DialogProvider>
      <Routes>
        {/* Public area */}
        <Route element={<PublicLayout />}>
          <Route path="/" element={<HomeRoute />} />
          <Route path="/landing" element={<Landing />} />
          <Route path="/partners" element={<Partners />} />
          <Route path="/mechanics" element={<GameMechanics />} />
          <Route path="/classes" element={<ClassesAndRanks />} />
          <Route path="/classes-ranks" element={<Navigate to="/classes" replace />} />
          <Route path="/ranks" element={<RanksGuide />} />
          <Route path="/faq" element={<FAQ />} />
          <Route path="/categories" element={<CategoriesGuide />} />
          <Route path="/good-deeds" element={<GoodDeedExamples />} />
          <Route path="/legal" element={<Legal />} />
        </Route>

        {/* 🔐 AUTH PAGES */}
        <Route path="/register" element={<Register />} />
        <Route path="/login" element={<Login />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/confirm-email" element={<ConfirmEmail />} />
        <Route path="/reset-password" element={<ResetPassword />} />

        {/* 🗡️ IN-APP ADVENTURER PORTAL — requires "Adventurer" role
            GuildAdmin and SystemAdmin also receive this role claim in their JWT */}
        <Route element={<ProtectedRoute requiredRole="Adventurer" />}>
          <Route element={<Layout />}>
            <Route path="/dashboard" element={<Dashboard />} />
            <Route path="/app/categories/:categoryKey" element={<CategoryDetail />} />
            <Route path="/level-statistics" element={<LevelStatistics />} />
            <Route path="/talents" element={<Talents />} />
            <Route path="/heroic-actions" element={<HeroicActions />} />
            <Route path="/heroic-actions/slots" element={<HeroicActionSlots />} />
            <Route path="/heroic-actions/quick" element={<QuickHeroicAction />} />
            <Route path="/heroic-actions/submissions" element={<HeroicActions />} />
            <Route path="/heroic-actions/submissions/:submissionId" element={<HeroicActions />} />
            <Route path="/heroic-actions/rules" element={<HeroicActions />} />
            <Route path="/heroic-actions/events" element={<EventsPage />} />
            <Route path="/heroic-actions/events/:eventId" element={<EventsPage />} />
            <Route path="/tribunal" element={<AdminHeroicActions showApplications={false} />} />
            <Route path="/tribunal/:submissionId" element={<AdminHeroicActions showApplications={false} />} />
            <Route path="/tavern" element={<Tavern />} />
            <Route path="/tavern/news/:newsId" element={<SystemNewsDetail />} />
            <Route path="/guilds" element={<Guilds />} />
            <Route path="/guilds/create" element={<CreateGuild />} />
            <Route path="/guilds/:guildId" element={<GuildDetail />} />
            <Route path="/guilds/:guildId/group-action-guide" element={<GroupActionGuide />} />
            <Route path="/guilds/:guildId/group-actions/new" element={<NewGuildHeroicAction />} />
            <Route path="/guilds/:guildId/:section" element={<GuildDetail />} />
            <Route path="/profile/redemptions" element={<AdventurerRedemptions />} />
            <Route path="/partners/:partnerId" element={<PartnerDetail />} />
            <Route path="/map" element={<WorldMap />} />
            <Route path="/profile" element={<Profile />} />
            <Route path="/adventurers/:userId" element={<PublicProfile />} />
            <Route path="/notifications" element={<Notifications />} />
            <Route path="/chats" element={<Chats />} />
            <Route path="/app/mechanics" element={<GameMechanics />} />
            <Route path="/app/categories" element={<CategoriesGuide />} />
            <Route path="/app/classes" element={<ClassesAndRanks />} />
            <Route path="/app/classes-ranks" element={<Navigate to="/app/classes" replace />} />
            <Route path="/app/ranks" element={<RanksGuide />} />
            <Route path="/app/faq" element={<FAQ />} />
            <Route path="/app/legal" element={<Legal />} />
          </Route>
        </Route>

        {/* 🛡️ SYSTEM ADMIN PORTAL — requires "SystemAdmin" role */}
        <Route element={<ProtectedRoute requiredRole="SystemAdmin" />}>
          <Route path="/admin" element={<SystemAdminLayout />}>
            <Route index element={<AdminDashboard />} />
            <Route path="dashboard" element={<AdminDashboard />} />
            <Route path="settings" element={<AdminSettings />} />
            <Route path="karma" element={<AdminKarma />} />
            <Route path="tribunal-moderators" element={<AdminTribunalModerators />} />
            <Route path="users" element={<AdminDirectory key="users" />} />
            <Route path="users/:userId" element={<AdminUserDetail />} />
            <Route path="partners" element={<AdminPartners />} />
            <Route path="partner-requests" element={<Navigate to="/admin/partners" replace />} />
            <Route path="guilds" element={<AdminDirectory key="guilds" guilds />} />
            <Route path="class-categories" element={<AdminClassCategoryManager />} />
            <Route path="events" element={<AdminEvents />} />
            <Route path="heroic-actions" element={<AdminHeroicActions />} />
            <Route path="heroic-actions/:submissionId" element={<AdminHeroicActions />} />
            <Route path="appeals" element={<AdminAppeals />} />
            <Route path="appeals/:appealId" element={<AdminAppeals />} />
            <Route path="xp-audits" element={<AdminXpAudits />} />
            <Route path="profile" element={<Profile />} />
          </Route>
        </Route>

        {/* 🏪 PARTNER PORTAL — requires "Partner" role */}
        <Route element={<ProtectedRoute requiredRole="Partner" />}>
          <Route path="/partner" element={<PartnerLayout />}>
            <Route index element={<Navigate to="/partner/pos" replace />} />
            <Route path="pos" element={<PartnerPOS />} />
            <Route path="offers" element={<PartnerOffers />} />
            <Route path="offers/new" element={<PartnerOfferEditor />} />
            <Route path="offers/:offerId/edit" element={<PartnerOfferEditor />} />
            <Route path="redemptions" element={<PartnerRedemptions />} />
          <Route path="profile" element={<PartnerProfile />} />
          <Route path="adventurers/:userId" element={<PublicProfile />} />
            <Route path="public/:partnerId" element={<PartnerDetail partnerPreview />} />
            <Route path="map" element={<WorldMap partnerPreview />} />
            <Route path="settings" element={<PartnerSettings />} />
          </Route>
        </Route>

        {/* 404 */}
        <Route element={<Layout />}><Route path="*" element={<ErrorPage status={404} />} /></Route>
      </Routes>
      <CookieBanner />
      </DialogProvider>
    </PresentationProvider>
    </Router>
  );
}

export default App;
